import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './ghs-jump-navigation.css.lit';
import { MobileController } from '../../controllers/mobile-controller';
import { classMap } from 'lit/directives/class-map.js';

type JumpItem = {
  label: string;
  id: string;
};

type ComponentVariant = 'default' | 'horizontal';

/**
 * Jump navigation component
 *
 * @element ghs-jump-navigation
 */
@customElement('ghs-jump-navigation')
export class GhsJumpNavigation extends OutlineElement {
  private mobileController = new MobileController(this);
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Component variant.
   */
  @property({ type: String, reflect: true })
  variant: ComponentVariant = 'default';

  /**
   * The array of items.
   */
  @property({ type: Array, reflect: false })
  items: JumpItem[] = [];

  /**
   * Auto set items by listening jump-here events.
   */
  @property({ type: Boolean, attribute: 'auto-set' })
  autoSet: false;

  addItem(event: CustomEvent) {
    if (event.detail && event.detail.label && event.detail.id) {
      this.items.push(event.detail);
      this.requestUpdate();
    }
  }

  firstUpdated() {
    super.firstUpdated();
    if (this.autoSet) {
      // @ts-expect-error Type 'Event' is missing 'detail' property from type 'CustomEvent'.
      window.addEventListener('jump-here', event => this.addItem(event));
    }
  }

  isAvailable(elementId: string) {
    return this.autoSet || !!document.getElementById(elementId);
  }

  jump(e: Event) {
    e.preventDefault();
    const elementId =
      // @ts-expect-error 'getAttribute' is not defined on 'EventTarget' interface.
      e.target?.value ?? e.target?.getAttribute('data-element-id');
    if (elementId) {
      document
        .getElementById(elementId)
        ?.scrollIntoView({ behavior: 'smooth' });
      window.history.replaceState({}, document.title, `#${elementId}`);
    }
  }

  renderItems(): TemplateResult {
    return html`
      ${this.items.map(item => item.id && item.label && this.renderItem(item))}
    `;
  }

  renderItem(item: JumpItem): TemplateResult {
    const isAvailable = this.isAvailable(item.id);
    return this.mobileController.isMobile
      ? html`
          <option
            class="jump-label ${isAvailable ? '' : 'disabled'}"
            value="${isAvailable ? item.id : ''}"
          >
            ${item.label}
          </option>
        `
      : html`
          <a
            class="jump-label ${isAvailable ? '' : 'disabled'}"
            data-element-id="${isAvailable ? item.id : ''}"
            @click="${this.jump}"
            href="#${isAvailable ? item.id : ''}"
          >
            <ghs-icon name="chevron-right" size="16px"></ghs-icon>
            ${item.label}
          </a>
        `;
  }

  render(): TemplateResult {
    const classes = {
      'jump-navigation': true,
      [`${this.variant}`]: true,
      'is-mobile': this.mobileController.isMobile,
    };
    return html`
      <div class=${classMap(classes)}>
        ${this.autoSet
          ? html` <div class="heading">On this page:</div> `
          : nothing}
        <div class="items">
          ${this.mobileController.isMobile
            ? html`<ghs-form content-no-padding
                ><select @change="${this.jump}">
                  ${this.renderItems()}
                </select></ghs-form
              >`
            : this.renderItems()}
        </div>
      </div>
    `;
  }
}
